import React from 'react'
import LoadingSpinner from '../../ui/LoadingSpinner'
import PassMappingLearnedBanner from './PassMappingLearnedBanner'
import {
  passMappingKeyToTestId,
  type PassMappingRow
} from '../../../utils/weldUpload/passMappingKeys'

interface PassMappingPanelProps {
  /** Torch-aware rows (preferred for TXT telemetry upload). */
  mappingRows?: PassMappingRow[]
  /** Plain pass names without torch — used by ALX / Data360 rename when rows are not torch-scoped. */
  rawPassNames?: string[]
  passMappings: Record<string, string>
  wpsPassLabels: string[]
  onMappingChange: (mappingKey: string, canonical: string) => void
  disabled?: boolean
  loading?: boolean
  learnedPrefillCount?: number
  /** Label for the zone column header (e.g. "TXT zone" or "ALX pass"). */
  sourceLabel?: string
  /** Tighter header when nested in accordion. */
  compact?: boolean
}

function rowsFromProps(
  mappingRows: PassMappingRow[] | undefined,
  rawPassNames: string[] | undefined
): PassMappingRow[] {
  if (mappingRows && mappingRows.length > 0) return mappingRows
  return (rawPassNames ?? []).map((passRaw) => ({ key: passRaw, passRaw }))
}

export default function PassMappingPanel({
  mappingRows,
  rawPassNames,
  passMappings,
  wpsPassLabels,
  onMappingChange,
  disabled = false,
  loading = false,
  learnedPrefillCount = 0,
  sourceLabel = 'Zone name',
  compact = false
}: PassMappingPanelProps) {
  const rows = rowsFromProps(mappingRows, rawPassNames)
  const passCount = rows.length
  const mappedCount = rows.filter((row) => (passMappings[row.key] ?? '') !== '').length
  const controlsDisabled = disabled || loading
  const hasTorchRows = rows.some((row) => row.torch)

  if (passCount === 0) return null

  return (
    <>
      <PassMappingLearnedBanner learnedPrefillCount={learnedPrefillCount} />
      <div
        data-testid="rename-pass-panel"
        className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
      >
        <div
          className={`flex items-center justify-between border-b border-gray-200 bg-gray-50 ${
            compact ? 'px-3 py-2' : 'px-4 py-2.5'
          }`}
        >
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
              Rename Pass
            </p>
            {!compact ? (
              <p className="mt-0.5 text-[11px] text-gray-500">
                {hasTorchRows
                  ? 'Map each Lead and Trail zone name to a WPS label.'
                  : 'Map each pass to a WPS label.'}
              </p>
            ) : null}
          </div>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
              loading
                ? 'bg-gray-50 text-gray-600 ring-gray-200'
                : mappedCount === passCount
                  ? 'bg-success-50 text-success-700 ring-success-200'
                  : 'bg-warning-50 text-warning-700 ring-warning-200'
            }`}
          >
            {loading ? 'Loading…' : `${mappedCount}/${passCount} mapped`}
          </span>
        </div>
        {loading ? (
          <div
            className="flex flex-col items-center justify-center gap-2 px-4 py-8"
            data-testid="rename-pass-loading"
          >
            <LoadingSpinner size="sm" />
            <p className="text-xs text-gray-500">Loading suggested mappings…</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((row) => {
              const mapped = (passMappings[row.key] ?? '') !== ''
              const testIdSuffix = passMappingKeyToTestId(row.key)
              return (
                <li
                  key={row.key}
                  data-testid={`rename-pass-row-${testIdSuffix}`}
                  className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    {row.torch ? (
                      <span
                        className={`mb-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          row.torch === 'Lead'
                            ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200'
                            : 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200'
                        }`}
                      >
                        {row.torch}
                      </span>
                    ) : null}
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      {sourceLabel}
                    </p>
                    <p className="truncate text-sm font-medium text-gray-900">{row.passRaw}</p>
                  </div>
                  <div className="flex min-w-0 flex-1 items-center gap-2 sm:max-w-md sm:justify-end">
                    <span className="hidden text-gray-300 sm:inline" aria-hidden>
                      →
                    </span>
                    <select
                      data-testid={`rename-pass-select-${testIdSuffix}`}
                      data-pass-map={row.key}
                      className={`block w-full rounded-md border px-2.5 py-1.5 text-sm shadow-sm focus:border-blueprint-blue focus:outline-none focus:ring-2 focus:ring-blueprint-blue/30 ${
                        mapped ? 'border-gray-300 bg-white' : 'border-warning-300 bg-warning-50'
                      }`}
                      value={passMappings[row.key] ?? ''}
                      onChange={(e) => onMappingChange(row.key, e.target.value)}
                      disabled={controlsDisabled}
                    >
                      <option value="">Unmapped</option>
                      {wpsPassLabels.map((label) => (
                        <option key={label} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </>
  )
}
